import {
  formatDateShort,
  formatMoney,
  formatNumber,
  formatRate,
  type Currency,
  type HouseholdDto,
  type MeDto,
  type MemberDto,
  type NumberLocale,
} from '@koverta/shared';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface Fmt {
  locale: NumberLocale;
  base: Currency;
  /** Grouped number, base minor units. Default 0 decimals (Plan, targets, tiles). */
  num: (minor: number, decimals?: 0 | 2, sign?: 'auto' | 'always') => string;
  /** Currency amount. RSD defaults to 0 decimals; EUR always 2. */
  money: (minor: number, currency?: Currency, decimals?: 0 | 2, sign?: 'auto' | 'always') => string;
  rate: (rate4: number) => string;
  date: (iso: string) => string;
}

export function makeFmt(locale: NumberLocale, base: Currency): Fmt {
  return {
    locale,
    base,
    num: (minor, decimals = 0, sign = 'auto') => formatNumber(minor, { locale, decimals, sign }),
    money: (minor, currency = base, decimals, sign = 'auto') =>
      formatMoney(minor, currency, { locale, decimals: decimals ?? (currency === 'EUR' ? 2 : 0), sign }),
    rate: (rate4) => formatRate(rate4, locale),
    date: (iso) => formatDateShort(iso, locale),
  };
}

interface Ctx {
  me: MeDto;
  household: HouseholdDto;
  member: MemberDto;
  members: Map<string, MemberDto>;
  partner: MemberDto | null;
  today: string;
  fmt: Fmt;
}

const HouseholdContext = createContext<Ctx | null>(null);

export function HouseholdProvider({ me, children }: { me: MeDto; children: ReactNode }) {
  const value = useMemo<Ctx>(() => {
    const household = me.household!;
    const members = new Map(household.members.map((m) => [m.id, m]));
    return {
      me,
      household,
      member: me.member!,
      members,
      partner: household.members.find((m) => m.id !== me.member!.id) ?? null,
      today: me.today,
      fmt: makeFmt(household.numberLocale, household.baseCurrency),
    };
  }, [me]);
  return <HouseholdContext.Provider value={value}>{children}</HouseholdContext.Provider>;
}

export function useHousehold() {
  const c = useContext(HouseholdContext);
  if (!c) throw new Error('useHousehold outside HouseholdProvider');
  return c;
}

export const useFmt = () => useHousehold().fmt;
